import { AppRole, CustomRoute } from "@/interfaces";
import DashboardLayout from "@/layout/DashboardLayout";
import GuestLayout from "@/layout/GuestLayout";
import RootLayout from "@/layout/RootLayout";
import { Suspense } from "react";
import { createBrowserRouter, Navigate, RouteObject } from "react-router";
import { getAdminRoutes } from "./admin";
import { getCurrentUserRouter } from "./CurrentRoutes";
import employeeRoutes from "./employee";
import financeRoutes from "./finance";
import guestRoutes from "./guest";
import { LoadingFallback } from "./router-utils";
import superAdminRoutes from "./superAdmin";

/**
 * Get routes based on user role
 * @param role - User role
 * @returns Array of CustomRoute for the specified role
 */
/**
 * Get routes based on user role
 * @param role - User role
 * @param user - User object (optional)
 * @returns Array of CustomRoute for the specified role
 */
export const getRoutesByRole = (role: AppRole, user?: any): CustomRoute[] => {
  const normalizedRole = role;

  switch (normalizedRole) {
    case "Super":
      return superAdminRoutes;
    case "Admin":
      return getAdminRoutes();
    case "Employee":
      return employeeRoutes;
    case "Finance":
      return financeRoutes;
    case "User":
      // Pass user object to determine if vendor or client routes should be returned
      return getCurrentUserRouter(role, user);
    default:
      return guestRoutes;
  }
};

export const createRoleRouter = (role: AppRole, user?: any) => {
  const routes = getRoutesByRole(role, user);
  const routeObjects = convertToRouteObjects(routes);

  // Add catch-all redirect for authenticated users
  if (role !== "Unauthenticated") {
    routeObjects.push({
      path: "*",
      element: <Navigate to="/" replace />,
    });
  }

  return createBrowserRouter([
    {
      path: "/",
      element: <RootLayout />,
      children: [
        {
          element: role === "Unauthenticated" ? <GuestLayout /> : <DashboardLayout />,
          children: routeObjects,
        },
      ],
    },
  ]);
};

/**
 * Convert CustomRoute array to react-router RouteObject array
 * Handles nested submenu routes and wraps lazy components in Suspense
 * @param routes - Array of CustomRoute
 * @returns Array of RouteObject for react-router
 */
const convertToRouteObjects = (routes: CustomRoute[]): RouteObject[] => {
  const routeObjects: RouteObject[] = [];

  const processRoute = (route: CustomRoute) => {
    // Skip routes without valid path or element
    if (!route.path || route.path.trim() === "" || !route.element) {
      // Process submenu if present
      if (route.submenu && route.submenu.length > 0) {
        route.submenu.forEach(processRoute);
      }
      return;
    }

    routeObjects.push({
      path: route.path,
      element: <Suspense fallback={<LoadingFallback />}>{route.element}</Suspense>,
    });

    // Process submenu routes
    if (route.submenu && route.submenu.length > 0) {
      route.submenu.forEach(processRoute);
    }
  };

  routes.forEach(processRoute);
  return routeObjects;
};

/**
 * Create router for a specific user role
 * @param role - User role
 * @returns Browser router configured for the role
 */

/**
 * Default router (unauthenticated) - used as fallback
 */
const router = createRoleRouter("Unauthenticated");

export default router;
